import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { JoinClosed } from "@/components/app/portal/join/join-closed";
import { JoinExisting } from "@/components/app/portal/join/join-existing";
import { JoinInviteForm } from "@/components/app/portal/join/join-invite-form";
import { Skeleton } from "@/components/ui/skeleton";
import { refusalWords, signIn, signOut, useSession } from "@/lib/host/auth-client";
import { orpc } from "@/lib/orpc";
import { beginSession } from "@/lib/session/session";
import { Home } from "@/lib/public-icons";

export const Route = createFileRoute("/family-portal/join/$token")({
  head: () => ({ meta: [{ title: "Join the family portal | People AI" }] }),
  component: JoinFamilyPortal,
});

const OFFLINE = "Could not reach the server. Check your connection and try again.";

/** The page an emailed family invitation opens: the token is the credential, so everything here
 * is asked of the server with it and nothing is kept in the address bar afterwards. */
function JoinFamilyPortal() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const [error, setError] = useState<string | null>(null);
  const preview = useQuery({
    ...orpc.public.familyInvites.preview.queryOptions({ input: { token } }),
    retry: false,
  });

  const enter = async () => {
    beginSession(queryClient);
    await navigate({ to: "/family-portal", replace: true });
  };

  const create = useMutation({
    mutationFn: async (values: { name: string; password: string }) => {
      const made = await orpc.public.familyInvites.accept.call({ token, ...values });
      const result = await signIn(made.email, values.password);
      if (result.error) throw new Error(refusalWords(result.error, OFFLINE));
    },
    onSuccess: enter,
    onError: (failure) => setError(failure.message),
  });

  const accept = useMutation(
    orpc.portal.acceptInvite.mutationOptions({
      onSuccess: enter,
      onError: (failure) => setError(failure.message),
    }),
  );

  const data = preview.data;
  return (
    <>
      {preview.isPending ? (
        <Skeleton className="mx-auto h-72 w-full max-w-md" aria-busy="true" />
      ) : preview.isError || !data ? (
        <JoinClosed reason="missing" />
      ) : data.state === "expired" || data.state === "revoked" || data.state === "accepted" ? (
        <JoinClosed reason={data.state} community={data.communityName ?? undefined} />
      ) : (
        <PortalNotice
          icon={<Home />}
          title={`Join ${data.communityName}`}
          action={
            data.state === "existing" ? (
              <JoinExisting
                token={token}
                email={data.email ?? ""}
                signedInAs={session.data?.user.email ?? null}
                pending={accept.isPending}
                error={error}
                onAccept={() => {
                  setError(null);
                  accept.mutate({ token });
                }}
                onSwitch={() => void signOut().then(() => session.refetch())}
              />
            ) : (
              <JoinInviteForm
                email={data.email ?? ""}
                pending={create.isPending}
                error={error}
                onSubmit={(values) => {
                  setError(null);
                  create.mutate(values);
                }}
              />
            )
          }
        >
          {data.inviterName
            ? `${data.inviterName} invited you to follow ${data.residentFirstName ?? "your family member"} here.`
            : `You are invited to follow ${data.residentFirstName ?? "your family member"} here.`}{" "}
          Photos, news, visits and paperwork are in one place.
        </PortalNotice>
      )}
    </>
  );
}
